import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ChevronDown, Search } from 'lucide-react';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { getSocket } from '../../lib/socket';
import { DailyCalendar, DailyCheckinView, WeeklyCheckinView, MonthlyReviewView } from './BusinessReviewHub';
import '../../styles/TeamDashboard.css';
import '../../styles/BusinessReviews.css';
import '../../styles/ManagerApprFeedback.css';
import '../../styles/BizReviewDetail.css';

// ── Manager select (custom dropdown — native <select> can't wrap long names) ───
// Renders its open panel into a portal on document.body: this control sits
// inside .tdb-hero-banner/.tdb-card, which sets overflow:hidden, so an
// absolutely-positioned panel would get clipped at the card's edge instead
// of floating above the page.
export function ManagerSelect({ value, onChange, options, placeholder = 'All Managers' }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rect, setRect] = useState(null);
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    function onOutside(e) {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    function updateRect() {
      if (btnRef.current) setRect(btnRef.current.getBoundingClientRect());
    }
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open]);

  const filtered = options.filter(o => !search || o.name.toLowerCase().includes(search.toLowerCase()));
  const selected = options.find(o => String(o.user_id) === String(value));

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" ref={btnRef}
        onClick={() => { setOpen(o => !o); setSearch(''); }}
        className="tdb-scope-select"
        style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', textAlign: 'left', width: 'auto', minWidth: 140, maxWidth: 200 }}>
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.name : placeholder}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
      </button>
      {open && rect && createPortal(
        <div ref={panelRef} style={{ position: 'fixed', top: rect.bottom + 4, left: rect.left, width: rect.width, maxWidth: 320, zIndex: 2000, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,.15)', display: 'flex', flexDirection: 'column', maxHeight: 260 }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--bg-muted)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 10px' }}>
              <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search…" style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: 13, width: '100%', color: 'var(--text-primary)' }} />
            </div>
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            <button type="button"
              onClick={() => { onChange(''); setOpen(false); setSearch(''); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, background: !value ? 'var(--bg-muted)' : 'transparent', color: 'var(--text-primary)', border: 'none', cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word' }}>
              All Managers
            </button>
            {filtered.length === 0
              ? <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-muted)' }}>No results</div>
              : filtered.map(o => (
                <button key={o.user_id} type="button"
                  onClick={() => { onChange(String(o.user_id)); setOpen(false); setSearch(''); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, background: String(o.user_id) === String(value) ? 'var(--bg-muted)' : 'transparent', color: 'var(--text-primary)', border: 'none', cursor: 'pointer', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {o.name}
                </button>
              ))
            }
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Hover tooltip for hero-banner stats, portaled to document.body with
// position:fixed. A pure-CSS absolute tooltip clipped to .tdb-hero-banner's
// overflow:hidden or, once un-clipped, spilled past the banner into the page
// content below with no visible connection to its trigger — portaling and
// computing position from the trigger's own rect avoids both problems.
function HeroTooltip({ text, children, ...rest }) {
  const [show, setShow] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!show) return;
    function updateRect() {
      if (ref.current) setRect(ref.current.getBoundingClientRect());
    }
    updateRect();
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [show]);

  return (
    <div
      {...rest}
      ref={ref}
      style={{ ...rest.style, position: 'relative' }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      {children}
      {show && rect && createPortal(
        <div style={{
          position: 'fixed', top: rect.bottom + 8, left: rect.left,
          background: 'var(--text-primary)', color: 'var(--bg-card)',
          fontSize: 11, fontWeight: 500, padding: '6px 10px', borderRadius: 6,
          width: 'max-content', maxWidth: 260, lineHeight: 1.4,
          zIndex: 3000, pointerEvents: 'none',
        }}>
          {text}
        </div>,
        document.body
      )}
    </div>
  );
}

// ── Time helpers ──────────────────────────────────────────────────────────────
function displayName(user) {
  if (!user) return 'Unknown';
  return [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || `#${user.id}`;
}
function fmtHours(minutes) {
  const m = Math.round(minutes || 0);
  const h = Math.floor(m / 60);
  const rem = m % 60;
  if (h === 0) return `${rem}m`;
  if (rem === 0) return `${h}h`;
  return `${h}h ${rem}m`;
}
function stripHtml(str) {
  if (!str) return '';
  return str.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').trim();
}

function getTimeAgo(dateString) {
  if (!dateString) return '';
  const diff = Date.now() - new Date(dateString).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  if (m > 0) return `${m}m ago`;
  return 'just now';
}

// ── Design tokens ─────────────────────────────────────────────────────────────
const CLR = {
  green:  '#4ad295',
  red:    '#ff6b6b',
  amber:  '#f0a93b',
  accent: '#06b6d4',
  blue:   '#3b82f6',
  purple: '#6f7bff',
  sky:    '#0ea5e9',
};

const AVATAR_COLORS = ['#6f7bff','#f0a93b','#4ad295','#c084fc','#06b6d4','#ff6b6b','#10b981'];
const avatarColor = (id) => AVATAR_COLORS[(id || 0) % AVATAR_COLORS.length];

const TAG_CLR = {
  task:    { bg: '#4ad29520', color: '#4ad295' },
  kudos:   { bg: '#f0a93b20', color: '#f0a93b' },
  comment: { bg: 'var(--bg-muted)', color: 'var(--text-muted)' },
  hours:   { bg: '#06b6d420', color: '#06b6d4' },
  post:    { bg: '#6f7bff20', color: '#6f7bff' },
};

// ── Last 12 months as { val: 'YYYY-MM', lbl: 'Month YYYY' } ──────────────────
function last12MonthOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const lbl = d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    opts.push({ val, lbl });
  }
  return opts;
}

// ── Arrow-only month picker ───────────────────────────────────────────────────
function MonthPickerGroup({ value, onChange, options, active, defaultValue, onReset, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const isDefault = value === defaultValue && !active;
  const displayLabel = label || (active && value !== defaultValue
    ? options.find(o => o.val === value)?.lbl?.replace(/\s\d{4}$/, '') || 'Month'
    : 'This Month');
  return (
    <div ref={ref} className="tdb-month-tab-group" style={{ position: 'relative' }}>
      <button
        className={`tdb-period-tab${active ? ' active' : ''}`}
        style={{ borderRadius: '6px 0 0 6px', borderRight: 'none' }}
        onClick={() => setOpen(o => !o)}
      >
        {displayLabel}
      </button>
      <button
        onClick={() => setOpen(o => !o)}
        className={`tdb-period-tab${active ? ' active' : ''}`}
        style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 2, borderRadius: '0 6px 6px 0' }}
        title="Select month"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, zIndex: 999,
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: 160, maxHeight: 240, overflowY: 'auto', marginTop: 4,
        }}>
          {options.map(o => (
            <div
              key={o.val}
              onClick={() => { onChange(o.val); setOpen(false); }}
              style={{
                padding: '8px 14px', fontSize: 12, cursor: 'pointer',
                fontWeight: o.val === value ? 700 : 400,
                color: o.val === value ? 'var(--accent)' : 'var(--text-primary)',
                background: o.val === value ? 'var(--accent-soft, #e0f2fe)' : 'transparent',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-muted)'}
              onMouseLeave={e => e.currentTarget.style.background = o.val === value ? 'var(--accent-soft, #e0f2fe)' : 'transparent'}
            >
              {o.lbl}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── FilterSelect — custom dropdown so long option text wraps inside a fixed-
// width menu instead of a native <select> popup stretching to fit content ──
function FilterSelect({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const ref = useRef(null);
  const MENU_W = 200;
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);
  const toggleOpen = () => {
    setOpen(o => {
      const next = !o;
      if (next && ref.current) {
        const rect = ref.current.getBoundingClientRect();

        // Keep the menu inside its containing card instead of spilling past
        // the card's right edge — align to the button's right edge when
        // there isn't room to grow rightward from its left edge.
        const container = ref.current.closest('.tdb-card') || document.body;
        const containerRight = container.getBoundingClientRect().right;
        setAlignRight(rect.left + MENU_W + 12 > containerRight);
      }
      return next;
    });
  };
  return (
    <div ref={ref} className="tdb-filter-select" style={{ position: 'relative' }}>
      <button
        type="button"
        className="tdb-month-select"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        onClick={toggleOpen}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || placeholder}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div className={`tdb-filter-select-menu${alignRight ? ' align-right' : ''}`}>
          <div
            className={`tdb-filter-select-option${value === '' ? ' active' : ''}`}
            onClick={() => { onChange(''); setOpen(false); }}
          >
            {placeholder}
          </div>
          {options.map(o => (
            <div
              key={o}
              className={`tdb-filter-select-option${o === value ? ' active' : ''}`}
              onClick={() => { onChange(o); setOpen(false); }}
            >
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── AreaSparkline ─────────────────────────────────────────────────────────────
function AreaSparkline({ data, color, uid = '' }) {
  if (!data || data.length < 2) return null;
  const W = 100, H = 40;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * W,
    H - 4 - ((v - min) / range) * (H - 10),
  ]);
  let line = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    const cx = ((pts[i - 1][0] + pts[i][0]) / 2).toFixed(1);
    line += ` C${cx},${pts[i-1][1].toFixed(1)} ${cx},${pts[i][1].toFixed(1)} ${pts[i][0].toFixed(1)},${pts[i][1].toFixed(1)}`;
  }
  const area = `${line} L${W},${H} L0,${H} Z`;
  const gId = `sg${uid}${color.replace(/[^a-z0-9]/gi, '')}`;
  const last = pts[pts.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '100%', display: 'block' }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="3" fill="var(--bg-card)" stroke={color} strokeWidth="1.8" />
    </svg>
  );
}

// ── KpiCard ────────────────────────────────────────────────────────────────────
function KpiCard({ title, value, sub, color, icon, sparkData }) {
  return (
    <div className="tdb-card tdb-kpi-mini" style={{ borderTop: `3px solid ${color}` }}>
      <div className="tdb-kpi-mini-header">
        <div className="tdb-kpi-mini-title">{title}</div>
        <div className="tdb-kpi-mini-icon" style={{ background: `${color}22` }}>
          {icon}
        </div>
      </div>
      <div className="tdb-kpi-mini-val" style={{ color }}>{value}</div>
      {sub && <div className="tdb-kpi-mini-sub">{sub}</div>}
      {sparkData && sparkData.length > 1 && (
        <div className="tdb-sparkline-wrap">
          <AreaSparkline data={sparkData} color={color} uid={title.replace(/\s/g, '')} />
        </div>
      )}
    </div>
  );
}

// ── TaskDonut — 4-segment with center label ────────────────────────────────────
function TaskDonut({ completed, inProgress, onHold, overdue, total }) {
  const r = 44;
  const C = 2 * Math.PI * r;
  const safe = total > 0 ? total : 1;
  const seg = (n) => (n / safe) * C;
  const cDash  = seg(completed);
  const pDash  = seg(inProgress);
  const hDash  = seg(onHold);
  const oDash  = seg(overdue);
  const cAngle = -90;
  const pAngle = cAngle + (completed / safe) * 360;
  const hAngle = pAngle + (inProgress / safe) * 360;
  const oAngle = hAngle + (onHold / safe) * 360;
  return (
    <svg width="160" height="160" viewBox="0 0 108 108">
      <circle cx="54" cy="54" r={r} fill="none" stroke="var(--bg-muted)" strokeWidth="11" />
      {cDash > 0.5 && (
        <circle cx="54" cy="54" r={r} fill="none" stroke={CLR.green} strokeWidth="11"
          strokeDasharray={`${cDash} ${C}`} transform={`rotate(${cAngle} 54 54)`} />
      )}
      {pDash > 0.5 && (
        <circle cx="54" cy="54" r={r} fill="none" stroke={CLR.blue} strokeWidth="11"
          strokeDasharray={`${pDash} ${C}`} transform={`rotate(${pAngle} 54 54)`} />
      )}
      {hDash > 0.5 && (
        <circle cx="54" cy="54" r={r} fill="none" stroke={CLR.amber} strokeWidth="11"
          strokeDasharray={`${hDash} ${C}`} transform={`rotate(${hAngle} 54 54)`} />
      )}
      {oDash > 0.5 && (
        <circle cx="54" cy="54" r={r} fill="none" stroke={CLR.red} strokeWidth="11"
          strokeDasharray={`${oDash} ${C}`} transform={`rotate(${oAngle} 54 54)`} />
      )}
    </svg>
  );
}

// ── MoodGauge — semi-circle with emoji center ─────────────────────────────────
function MoodGauge({ score }) {
  const r = 40, cx = 55, cy = 58;
  const C_half = Math.PI * r;
  const pct = Math.min(1, Math.max(0, score / 10));
  const color = score >= 8 ? CLR.green : score >= 6 ? CLR.amber : CLR.red;
  const x1 = cx - r, x2 = cx + r;
  const emoji = score >= 8 ? '😄' : score >= 6 ? '🙂' : '😟';
  return (
    <div className="tdb-mood-gauge-wrap">
      <svg width="110" height="72" viewBox="0 0 110 72">
        <path d={`M${x1},${cy} A${r},${r} 0 0,1 ${x2},${cy}`}
          fill="none" stroke="var(--bg-muted)" strokeWidth="11" strokeLinecap="round" />
        <path d={`M${x1},${cy} A${r},${r} 0 0,1 ${x2},${cy}`}
          fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
          strokeDasharray={`${pct * C_half} ${C_half}`} />
      </svg>
      <div className="tdb-mood-emoji-wrap">
        <span className="tdb-mood-emoji">{emoji}</span>
      </div>
    </div>
  );
}

// ── HeroIllustration ──────────────────────────────────────────────────────────
function HeroIllustration() {
  return (
    <svg width="220" height="120" viewBox="0 0 220 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Background circles */}
      <circle cx="170" cy="50" r="70" fill="var(--accent-soft)" opacity="0.5" />
      <circle cx="140" cy="80" r="45" fill="var(--accent-soft)" opacity="0.4" />

      {/* Person figures */}
      <circle cx="60" cy="38" r="13" fill="var(--accent)" opacity="0.85" />
      <rect x="47" y="54" width="26" height="32" rx="6" fill="var(--accent)" opacity="0.75" />

      <circle cx="110" cy="30" r="15" fill={CLR.green} opacity="0.85" />
      <rect x="95" y="48" width="30" height="36" rx="6" fill={CLR.green} opacity="0.75" />

      <circle cx="160" cy="38" r="13" fill={CLR.purple} opacity="0.85" />
      <rect x="147" y="54" width="26" height="32" rx="6" fill={CLR.purple} opacity="0.75" />

      {/* Chat / task bubbles */}
      <rect x="30" y="8" width="52" height="18" rx="9" fill="var(--bg-card)" stroke="var(--border)" strokeWidth="1.2" />
      <circle cx="42" cy="17" r="3.5" fill={CLR.amber} />
      <rect x="50" y="13" width="24" height="3" rx="1.5" fill="var(--border)" />
      <rect x="50" y="18" width="16" height="2" rx="1" fill="var(--border)" opacity="0.6" />

      <rect x="138" y="4" width="56" height="18" rx="9" fill="var(--bg-card)" stroke="var(--border)" strokeWidth="1.2" />
      <circle cx="150" cy="13" r="3.5" fill={CLR.green} />
      <rect x="158" y="9" width="28" height="3" rx="1.5" fill="var(--border)" />
      <rect x="158" y="14" width="18" height="2" rx="1" fill="var(--border)" opacity="0.6" />

      {/* Progress bar card */}
      <rect x="70" y="92" width="80" height="24" rx="6" fill="var(--bg-card)" stroke="var(--border)" strokeWidth="1" />
      <rect x="77" y="98" width="50" height="4" rx="2" fill="var(--bg-muted)" />
      <rect x="77" y="98" width="36" height="4" rx="2" fill={CLR.green} />
      <rect x="77" y="106" width="36" height="3" rx="1.5" fill="var(--bg-muted)" />
      <rect x="77" y="106" width="20" height="3" rx="1.5" fill="var(--accent)" />

      {/* Stars/sparkle */}
      <circle cx="198" cy="22" r="3" fill={CLR.amber} opacity="0.9" />
      <circle cx="208" cy="40" r="2" fill={CLR.green} opacity="0.7" />
      <circle cx="190" cy="60" r="2.5" fill="var(--accent)" opacity="0.7" />
    </svg>
  );
}

// ── Mock Data ─────────────────────────────────────────────────────────────────
const MOCK_DATA = {
  members: [
    {
      user: { id: 101, first_name: 'Alex', last_name: 'Rivera', email: 'alex.rivera@company.com' },
      roles: [{ title: 'Lead Backend Architect', level: 4 }],
      hours: { today: { minutes: 480 }, this_week: { minutes: 2400 }, this_month: { minutes: 9600 }, this_year: { minutes: 86400 } },
      tasks: { total: 18, done: 12, in_progress: 4, open: 1, blocked: 1, overdue: 0, not_started_est_hrs: 2.5, in_progress_est_hrs: 6.0 },
      review_health: { color: 'gold', score: 4.8, band: 5, label: 'Outstanding' },
      engagement_trend: [
        { month: '2026-01', score: 85 }, { month: '2026-02', score: 90 },
        { month: '2026-03', score: 88 }, { month: '2026-04', score: 94 }, { month: '2026-05', score: 97 },
      ],
    },
    {
      user: { id: 102, first_name: 'Sarah', last_name: 'Chen', email: 'sarah.chen@company.com' },
      roles: [{ title: 'Senior Frontend Engineer', level: 3 }],
      hours: { today: { minutes: 420 }, this_week: { minutes: 2200 }, this_month: { minutes: 8800 }, this_year: { minutes: 79200 } },
      tasks: { total: 15, done: 10, in_progress: 3, open: 2, blocked: 0, overdue: 1, not_started_est_hrs: 4.0, in_progress_est_hrs: 3.5 },
      review_health: { color: 'green', score: 4.2, band: 4, label: 'Exceeding Expectations' },
      engagement_trend: [
        { month: '2026-01', score: 75 }, { month: '2026-02', score: 82 },
        { month: '2026-03', score: 80 }, { month: '2026-04', score: 85 }, { month: '2026-05', score: 89 },
      ],
    },
    {
      user: { id: 103, first_name: 'Marcus', last_name: 'Vance', email: 'marcus.vance@company.com' },
      roles: [{ title: 'DevOps & Cloud Engineer', level: 3 }],
      hours: { today: { minutes: 360 }, this_week: { minutes: 2000 }, this_month: { minutes: 8000 }, this_year: { minutes: 72000 } },
      tasks: { total: 12, done: 8, in_progress: 2, open: 1, blocked: 1, overdue: 0, not_started_est_hrs: 1.5, in_progress_est_hrs: 2.0 },
      review_health: { color: 'amber', score: 3.5, band: 3, label: 'Meeting Expectations' },
      engagement_trend: [
        { month: '2026-01', score: 65 }, { month: '2026-02', score: 70 },
        { month: '2026-03', score: 72 }, { month: '2026-04', score: 68 }, { month: '2026-05', score: 75 },
      ],
    },
    {
      user: { id: 104, first_name: 'Elena', last_name: 'Rostova', email: 'elena.rostova@company.com' },
      roles: [{ title: 'Product UI/UX Designer', level: 3 }],
      hours: { today: { minutes: 120 }, this_week: { minutes: 1800 }, this_month: { minutes: 7200 }, this_year: { minutes: 64800 } },
      tasks: { total: 14, done: 9, in_progress: 3, open: 2, blocked: 0, overdue: 0, not_started_est_hrs: 3.0, in_progress_est_hrs: 4.5 },
      review_health: { color: 'green', score: 4.0, band: 4, label: 'Exceeding Expectations' },
      engagement_trend: [
        { month: '2026-01', score: 80 }, { month: '2026-02', score: 85 },
        { month: '2026-03', score: 83 }, { month: '2026-04', score: 87 }, { month: '2026-05', score: 88 },
      ],
    },
    {
      user: { id: 105, first_name: 'David', last_name: 'Kim', email: 'david.kim@company.com' },
      roles: [{ title: 'QA Automation Engineer', level: 2 }],
      hours: { today: { minutes: 0 }, this_week: { minutes: 1200 }, this_month: { minutes: 4800 }, this_year: { minutes: 43200 } },
      tasks: { total: 20, done: 14, in_progress: 4, open: 2, blocked: 0, overdue: 0, not_started_est_hrs: 5.0, in_progress_est_hrs: 7.0 },
      review_health: { color: 'green', score: 3.9, band: 4, label: 'Meeting Expectations' },
      engagement_trend: [
        { month: '2026-01', score: 70 }, { month: '2026-02', score: 72 },
        { month: '2026-03', score: 75 }, { month: '2026-04', score: 78 }, { month: '2026-05', score: 82 },
      ],
    },
  ],
  team_totals: {
    member_count: 12,
    hours_this_week: 160.0,
    avg_hours_this_week: 32.0,
    total_overdue: 1,
    team_tasks: { completed: 53, in_progress: 16, on_hold: 4, delayed: 7, total: 80 },
    team_productivity: 87,
    project_progress: [
      { id: 1, title: 'API Integration Gateway', progress: 85 },
      { id: 2, title: 'UI Component Library Design System', progress: 68 },
      { id: 3, title: 'Automated CI/CD Pipeline', progress: 92 },
      { id: 4, title: 'Database Optimization', progress: 45 },
    ],
    team_activity: [
      { actor_name: 'Alex Rivera', action: 'completed task', item: 'Optimize DB index patterns for query speedups', ts: new Date(Date.now() - 900000).toISOString(), tag: 'task' },
      { actor_name: 'Sarah Chen', action: 'commented', item: 'Design System feedback on button variations', ts: new Date(Date.now() - 2700000).toISOString(), tag: 'comment' },
      { actor_name: 'Marcus Vance', action: 'logged time', item: 'Configure SSL certificates for Staging environment', ts: new Date(Date.now() - 7200000).toISOString(), tag: 'hours' },
      { actor_name: 'David Kim', action: 'posted', item: '98% coverage in automated UI integration tests!', ts: new Date(Date.now() - 14400000).toISOString(), tag: 'post' },
      { actor_name: 'Elena Rostova', action: 'gave kudos', item: 'Thank you Alex for backend API coordination!', ts: new Date(Date.now() - 28800000).toISOString(), tag: 'kudos' },
    ],
    team_deadlines: [],
    team_mood: 8.4,
  },
};

// ── Team Routine Analysis ─────────────────────────────────────────────────────
function TeamRoutineAnalysis({ appId, api, scope, selectedManagerId, memberSearch }) {
  const navigate = useNavigate();
  const routineAnalysisNavTarget = (() => {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    if (scope === 'all' && selectedManagerId) params.set('view_as', selectedManagerId);
    const qs = params.toString();
    return `/apps/${appId}/team-routines${qs ? `?${qs}` : ''}`;
  })();
  const defaultMonth = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
  const [currentMonth, setCurrentMonth] = useState(defaultMonth);
  const monthOpts = last12MonthOptions();
  const isCurrentMonth = currentMonth === defaultMonth;

  const monthNav = (
    <MonthPickerGroup
      value={currentMonth}
      active={!isCurrentMonth}
      options={monthOpts}
      onChange={setCurrentMonth}
      defaultValue={defaultMonth}
      label={!isCurrentMonth ? monthOpts.find(o => o.val === currentMonth)?.lbl?.replace(/\s\d{4}$/, '') || 'Month' : 'This Month'}
    />
  );

  const [rData, setRData] = useState(null);
  const [rLoad, setRLoad] = useState(true);
  const [rErr,  setRErr]  = useState(false);
  const [rtView, setRtView] = useState('routines'); // 'routines' | 'areas'
  const [agScrollbarW, setAgScrollbarW] = useState(0); // Areas grid: body scrollbar width, mirrored onto the header

  useEffect(() => {
    setRLoad(true);
    const extra = { mode: 'month', month: currentMonth };
    if (scope) extra.scope = scope;
    api.getTeamRoutineDashboard(appId, undefined, extra, scope === 'all' ? selectedManagerId : null)
      .then(setRData)
      .catch(() => setRErr(true))
      .finally(() => setRLoad(false));
  }, [appId, currentMonth, scope, selectedManagerId]);

  if (rLoad) return (
    <div className="tdb-card tdb-ra-card" style={{ overflow: 'visible' }}>
      <div className="tdb-ra-header">
        <div className="tdb-ra-title-row">
          <div className="tdb-ra-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div className="tdb-ra-title">Routine Analysis</div>
        </div>
        {monthNav}
      </div>
      <div style={{ padding: '24px', fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
    </div>
  );
  if (rErr || !rData || !rData.reportees?.length) return null;

  const allReportees = rData.reportees;
  const reportees = memberSearch
    ? allReportees.filter(r => displayName(r.user).toLowerCase().includes(memberSearch))
    : allReportees;
  const withEff   = reportees.filter(r => r.overall_efficiency != null);
  const sorted    = [...withEff].sort((a, b) => b.overall_efficiency - a.overall_efficiency);
  const best      = sorted[0];
  const worst     = sorted[sorted.length - 1];

  // most missed routines across team
  const missedMap = {};
  for (const rep of reportees) {
    for (const rs of rep.routine_stats || []) {
      if (rs.status === 'incomplete' || rs.status === 'not_started' || rs.status === 'pending') {
        const key = rs.routine.description;
        missedMap[key] = (missedMap[key] || 0) + 1;
      }
    }
  }
  const topMissed = Object.entries(missedMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);

  const effColor = (e) => e >= 80 ? CLR.green : e >= 50 ? CLR.amber : CLR.red;

  // Summary stats
  // Quarterly/yearly routines not due yet this month (not_applicable) aren't counted.
  const totalRoutines = reportees.reduce((acc, r) => acc + (r.routine_stats || []).filter(s => s.status !== 'not_applicable').length, 0);
  const avgEff = withEff.length
    ? Math.round(withEff.reduce((a, r) => a + r.overall_efficiency, 0) / withEff.length)
    : 0;
  const needsAttention = withEff.filter(r => r.overall_efficiency < 50).length;

  // ── Routines | Areas toggle ────────────────────────────────────────────────
  // "Areas" lists every area across this scope (de-duped by name) with the
  // team's task-completion % for it — done ÷ total summed across all members'
  // rep.area_stats — plus how many people have work in that area.
  const teamAreas = (() => {
    const m = new Map();
    for (const rep of reportees) {
      for (const a of rep.area_stats || []) {
        const k = a.area_name.trim().toLowerCase();
        if (!m.has(k)) m.set(k, { area_id: a.area_id, area_name: a.area_name, done: 0, total: 0, people: 0 });
        const e = m.get(k);
        e.done += a.done; e.total += a.total;
        if (a.total > 0) e.people += 1;
      }
    }
    return [...m.values()]
      .map(e => ({ ...e, pct: e.total > 0 ? Math.min(100, Math.round((e.done / e.total) * 100)) : null }))
      .sort((a, b) => a.area_name.localeCompare(b.area_name));
  })();
  const showAreas = rtView === 'areas';
  const areaWithPct = teamAreas.filter(a => a.pct != null);
  const stripAvg   = showAreas
    ? (areaWithPct.length ? Math.round(areaWithPct.reduce((s, a) => s + a.pct, 0) / areaWithPct.length) : 0)
    : avgEff;
  // "Needs attention" = has tasks but isn't fully done — same threshold as
  // My Areas' Needs Attention bucket, so a member's area is never flagged on
  // one dashboard and not the other.
  const stripNeeds = showAreas ? areaWithPct.filter(a => a.pct < 100).length : needsAttention;
  const stripTotal = showAreas ? teamAreas.length : totalRoutines;

  return (
    <div className="tdb-card tdb-ra-card" style={{ overflow: 'visible' }}>
      {/* Header */}
      <div className="tdb-ra-header">
        <div className="tdb-ra-title-row">
          <div className="tdb-ra-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div className="tdb-ra-title">{showAreas ? 'Team Areas' : 'Routine Analysis'}</div>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          <div className="tdb-ra-tabs">
            <button
              className={`tdb-ra-tab${rtView === 'routines' ? ' active' : ''}`}
              onClick={() => setRtView('routines')}
            >Routines</button>
            <button
              className={`tdb-ra-tab${rtView === 'areas' ? ' active' : ''}`}
              onClick={() => setRtView('areas')}
            >Areas</button>
          </div>
          {monthNav}
          {!showAreas && (
            <button className="tdb-ra-view-all" onClick={() => navigate(routineAnalysisNavTarget)}>
              View All Routines →
            </button>
          )}
        </div>
      </div>

      {/* Stats strip — moved to top */}
      <div className="tdb-ra-footer">
        <div className="tdb-ra-stat">
          <div className="tdb-ra-stat-icon" style={{ background: '#6f7bff18', color: CLR.purple }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div className="tdb-ra-stat-info">
            <div className="tdb-ra-stat-val">{reportees.length}</div>
            <div className="tdb-ra-stat-lbl">Team Members</div>
          </div>
        </div>
        <div className="tdb-ra-stat">
          <div className="tdb-ra-stat-icon" style={{ background: '#f0a93b18', color: CLR.amber }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
          </div>
          <div className="tdb-ra-stat-info">
            <div className="tdb-ra-stat-val">{stripTotal}</div>
            <div className="tdb-ra-stat-lbl">{showAreas ? 'Areas' : 'Total Routines'}</div>
          </div>
        </div>
        <div className="tdb-ra-stat">
          <div className="tdb-ra-stat-icon" style={{ background: '#4ad29518', color: CLR.green }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <div className="tdb-ra-stat-info">
            <div className="tdb-ra-stat-val" style={{ color: stripAvg >= 80 ? CLR.green : stripAvg >= 50 ? CLR.amber : CLR.red }}>{stripAvg}%</div>
            <div className="tdb-ra-stat-lbl">{showAreas ? 'Avg Area Completion' : 'Average Completion'}</div>
          </div>
        </div>
        <div className="tdb-ra-stat">
          <div className="tdb-ra-stat-icon" style={{ background: '#ff6b6b18', color: CLR.red }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div className="tdb-ra-stat-info">
            <div className="tdb-ra-stat-val" style={{ color: CLR.red }}>{stripNeeds}</div>
            <div className="tdb-ra-stat-lbl">{showAreas ? 'Needs Attention' : 'Routines Need Attention'}</div>
          </div>
        </div>
      </div>

      {showAreas ? (
        /* Areas view — grid: member → their areas → completed/assigned this month */
        <div className="tdb-ra-body" style={{ display: 'block' }}>
          {(() => {
            // One row per team member (matches the "Team Members" count). Show
            // every area on their role — an area with no tasks this month shows
            // "—" instead of a count; a member whose role has no areas at all
            // still gets a row, with a single "—" band.
            const rows = reportees.map(rep => ({ rep, areas: rep.area_stats || [] }));
            if (rows.length === 0) return (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
                No team members in this scope.
              </div>
            );
            if (teamAreas.length === 0) return (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '20px 0' }}>
                No Areas Assigned to Team
              </div>
            );
            return (
              <div className="tdb-ag-wrap">
                <div className="tdb-ag-hscroll">
                  <div className="tdb-ag-thead" style={{ paddingRight: agScrollbarW }}>
                    <div className="tdb-ag-th tdb-ag-th-sno">Sl No</div>
                    <div className="tdb-ag-th tdb-ag-th-member">Member</div>
                    <div className="tdb-ag-th tdb-ag-th-stack">Areas</div>
                    <div className="tdb-ag-th tdb-ag-th-stack">Time Spent</div>
                    <div className="tdb-ag-th tdb-ag-th-stack">Completed / Assigned</div>
                  </div>
                  <div
                    className="tdb-ag-tbody"
                    ref={el => {
                      if (!el) return;
                      const w = el.offsetWidth - el.clientWidth;
                      if (w !== agScrollbarW) setAgScrollbarW(w);
                    }}
                  >
                    {rows.map(({ rep, areas }, idx) => {
                      const initial = (displayName(rep.user)[0] || '?').toUpperCase();
                      return (
                        <div key={rep.user.id} className="tdb-ag-row">
                          <div className="tdb-ag-td tdb-ag-td-sno">{idx + 1}</div>
                          <div className="tdb-ag-td tdb-ag-td-member">
                            <div className="tdb-ag-avatar" style={{ background: rep.user.photo_url ? 'transparent' : avatarColor(rep.user.id) }}>
                              {rep.user.photo_url
                                ? <img src={rep.user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                : initial}
                            </div>
                            <span className="tdb-ag-member-name" title={displayName(rep.user)}>{displayName(rep.user)}</span>
                          </div>
                          <div className="tdb-ag-td tdb-ag-td-stack">
                            {areas.length === 0
                              ? <span className="tdb-ag-line" style={{ color: 'var(--text-muted)' }}>No Areas</span>
                              : areas.map(a => (
                                  <span key={a.area_id} className="tdb-ag-line" title={a.area_name}>{a.area_name}</span>
                                ))}
                          </div>
                          <div className="tdb-ag-td tdb-ag-td-stack">
                            {areas.length === 0
                              ? <span className="tdb-ag-line" style={{ color: 'var(--text-muted)' }}>—</span>
                              : areas.map(a => (
                                  <span key={a.area_id} className="tdb-ag-line" style={{ color: a.minutes > 0 ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                                    {a.minutes > 0 ? fmtHours(a.minutes) : '—'}
                                  </span>
                                ))}
                          </div>
                          <div className="tdb-ag-td tdb-ag-td-stack">
                            {areas.length === 0
                              ? <span className="tdb-ag-line" style={{ color: 'var(--text-muted)' }}>—</span>
                              : (() => {
                                  // On Track = every task created in the area is done (3/3);
                                  // Needs Attention = created but not all done (2/3), or no
                                  // tasks at all. Same rule as My Dashboard's My Areas.
                                  return areas.map(a => {
                                    const needsAttention = a.total === 0 || a.done < a.total;
                                    return a.total > 0 ? (
                                      <span key={a.area_id} className="tdb-ag-line tdb-ag-line-stat">
                                        <span style={{ color: effColor(a.pct ?? 0) }}>{a.done} / {a.total}</span>
                                        {needsAttention
                                          ? <span className="tdb-am-badge attention">Needs Attention</span>
                                          : <span className="tdb-am-badge on-track">✓ On Track</span>}
                                      </span>
                                    ) : (
                                      <span key={a.area_id} className="tdb-ag-line tdb-ag-line-stat">
                                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                                        <span className="tdb-am-badge attention">Needs Attention</span>
                                      </span>
                                    );
                                  });
                                })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
      <div className="tdb-ra-body">

        {/* Left — completion bars */}
        <div className="tdb-ra-left">
          <div className="tdb-ra-section-label">Team Completion Rate</div>
          <div className="tdb-ra-members">
            {reportees.length === 0 && (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>No members match “{memberSearch}”.</div>
            )}
            {reportees.map(rep => {
              const eff = rep.overall_efficiency;
              const name = displayName(rep.user).split(' ')[0];
              const color = eff != null ? effColor(eff) : '#9ca3af';
              const initial = (displayName(rep.user)[0] || '?').toUpperCase();
              return (
                <div key={rep.user.id} className="tdb-ra-member-row">
                  <div className="tdb-ra-avatar" style={{ background: rep.user.photo_url ? 'transparent' : avatarColor(rep.user.id) }}>
                    {rep.user.photo_url
                      ? <img src={rep.user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : initial}
                  </div>
                  <div className="tdb-ra-member-name">{name}</div>
                  <div className="tdb-ra-bar-track">
                    <div className="tdb-ra-bar-fill" style={{ width: `${eff ?? 0}%`, background: color }} />
                  </div>
                  <div className="tdb-ra-member-pct" style={{ color }}>
                    {eff != null ? `${eff}%` : '—'}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div className="tdb-ra-legend">
            <span className="tdb-ra-legend-dot" style={{ background: CLR.green }} />Completed (&gt;90%)
            <span className="tdb-ra-legend-dot" style={{ background: CLR.amber, marginLeft: 12 }} />In Progress (1–90%)
            <span className="tdb-ra-legend-dot" style={{ background: CLR.red, marginLeft: 12 }} />Not Started (0%)
          </div>
        </div>

        {/* Right — highlights + missed */}
        <div className="tdb-ra-right">
          {/* Top Performer */}
          {best && (
            <div className="tdb-ra-highlight tdb-ra-highlight-best">
              <div className="tdb-ra-highlight-icon tdb-ra-highlight-icon-best">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={CLR.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
                </svg>
              </div>
              <div className="tdb-ra-highlight-info">
                <div className="tdb-ra-highlight-badge tdb-ra-badge-best">Top Performer</div>
                <div className="tdb-ra-highlight-name">{displayName(best.user)}</div>
              </div>
              <div className="tdb-ra-highlight-right">
                <div className="tdb-ra-highlight-caption">Completion Rate</div>
                <div className="tdb-ra-highlight-val" style={{ color: CLR.green }}>{best.overall_efficiency}%</div>
              </div>
            </div>
          )}

          {/* Needs Support */}
          {worst && worst.user.id !== best?.user.id && (
            <div className="tdb-ra-highlight tdb-ra-highlight-low">
              <div className="tdb-ra-highlight-icon tdb-ra-highlight-icon-low">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={CLR.red} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8Z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/>
                </svg>
              </div>
              <div className="tdb-ra-highlight-info">
                <div className="tdb-ra-highlight-badge tdb-ra-badge-low">Needs Support</div>
                <div className="tdb-ra-highlight-name">{displayName(worst.user)}</div>
              </div>
              <div className="tdb-ra-highlight-right">
                <div className="tdb-ra-highlight-caption">Completion Rate</div>
                <div className="tdb-ra-highlight-val" style={{ color: CLR.red }}>{worst.overall_efficiency}%</div>
              </div>
            </div>
          )}

          {/* Most Missed */}
          {topMissed.length > 0 && (
            <div className="tdb-ra-missed-card">
              <div className="tdb-ra-missed-header">
                <div className="tdb-ra-missed-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <span className="tdb-ra-section-label" style={{ marginBottom: 0 }}>Most Missed Routines</span>
              </div>
              <div className="tdb-ra-missed-list">
                {topMissed.map(([name, count]) => (
                  <div key={name} className="tdb-ra-missed-row">
                    <span className="tdb-ra-missed-name">{name}</span>
                    <span className="tdb-ra-missed-count" onClick={() => navigate(routineAnalysisNavTarget)}>
                      {count} member{count !== 1 ? 's' : ''}
                    </span>
                  </div>
                ))}
              </div>
              <button className="tdb-ra-missed-view-all" onClick={() => navigate(routineAnalysisNavTarget)}>
                View All Missed Routines →
              </button>
            </div>
          )}
        </div>
      </div>
      )}

    </div>
  );
}

// ── Team Review Calendar Widget ───────────────────────────────────────────────
function TeamReviewCalendarWidget({ appId, managerId, onOpenReview }) {
  const api = useApi();
  const [dailyReviews, setDailyReviews] = useState([]);
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    if (!managerId) return;
    const [y, m] = month.split('-').map(Number);
    const from = `${month}-01`;
    const to   = `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
    api.listBusinessReviews(appId, { type: 'daily', from, to, per_page: 100, manager_id: managerId })
      .then(d => setDailyReviews(d.reviews || []))
      .catch(() => {});
  }, [appId, month, managerId]);

  const [y, m] = month.split('-').map(Number);
  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  const shiftMonth = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '6px 16px 4px' }}>
        <button
          onClick={() => shiftMonth(-1)}
          style={{ border: 'none', background: 'none', padding: '2px 6px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 15, lineHeight: 1 }}
        >&#x2039;</button>
        <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg)' }}>{monthLabel}</span>
        <button
          onClick={() => shiftMonth(1)}
          style={{ border: 'none', background: 'none', padding: '2px 6px', cursor: 'pointer', color: 'var(--text-primary)', fontSize: 15, lineHeight: 1 }}
        >&#x203a;</button>
      </div>
      <DailyCalendar
        month={month}
        dailyReviews={dailyReviews}
        onDayClick={(id, type) => onOpenReview({ id, type })}
        onCreateForDate={() => {}}
        canCreate={false}
      />
    </div>
  );
}

// ── Appreciations & Feedback Section ─────────────────────────────────────────
function ApprFeedSection({ appId, api, scope, managerMode, selectedManagerId, memberSearch }) {
  const [stats, setStats]     = useState(null);
  const [loading, setLoading] = useState(true);
  const defaultMonth = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
  const [afMonth, setAfMonth] = useState(defaultMonth);
  const afMonthOpts = last12MonthOptions();
  const isCurrentAfMonth = afMonth === defaultMonth;

  const apiScope = scope === 'all' ? 'team' : scope; // direct | team | admin

  useEffect(() => {
    if (!appId || !managerMode) return;
    setLoading(true);
    const viewAs = scope === 'all' ? selectedManagerId : null;
    api.getTeamApprFeedbackStats(appId, apiScope, afMonth, viewAs)
      .then(setStats)
      .catch(err => console.error('getTeamApprFeedbackStats failed', apiScope, afMonth, err))
      .finally(() => setLoading(false));
  }, [appId, apiScope, managerMode, afMonth, selectedManagerId]);

  // The header sits outside .maf2-tbody-scroll, so it has no scrollbar of
  // its own to reserve space for — a CSS-guessed gutter width drifts on any
  // OS/browser whose actual scrollbar isn't exactly 4px. Measuring it live
  // (offsetWidth - clientWidth) keeps the header's columns pixel-aligned
  // with the body's regardless of what's actually rendered.
  const maf2TbodyRef = useRef(null);
  const [maf2ScrollbarW, setMaf2ScrollbarW] = useState(0);
  const maf2MemberCount = stats?.members?.length || 0;
  useEffect(() => {
    const el = maf2TbodyRef.current;
    if (!el) { setMaf2ScrollbarW(0); return; }
    setMaf2ScrollbarW(el.offsetWidth - el.clientWidth);
  }, [maf2MemberCount]);

  if (!managerMode) return null;

  const allMembers = stats?.members || [];
  const members = memberSearch
    ? allMembers.filter(m => (m.name || '').toLowerCase().includes(memberSearch))
    : allMembers;
  const totalAppr = memberSearch ? members.reduce((s, m) => s + m.appr_received, 0) : (stats?.total_appr ?? 0);
  const totalFb   = memberSearch ? members.reduce((s, m) => s + m.fb_received, 0)   : (stats?.total_fb ?? 0);

  function daysAgoLabel(days) {
    if (days === null || days === undefined) return 'Never';
    if (days === 0) return 'Today';
    if (days === 1) return '1 day ago';
    return `${days} days ago`;
  }
  function daysColor(days) {
    if (days === null || days === undefined) return 'var(--text-secondary)';
    if (days === 0) return CLR.green;
    if (days <= 7)  return CLR.green;
    if (days <= 30) return CLR.amber;
    return CLR.red;
  }
  function initials(name) {
    return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  }

  return (
    <div className="maf2-section">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 className="tdb-section-title" style={{ margin: 0 }}>Appreciations &amp; Feedback</h3>
        <MonthPickerGroup
          value={afMonth}
          active={!isCurrentAfMonth}
          options={afMonthOpts}
          onChange={setAfMonth}
          defaultValue={defaultMonth}
          label={!isCurrentAfMonth ? afMonthOpts.find(o => o.val === afMonth)?.lbl?.replace(/\s\d{4}$/, '') || 'Month' : 'This Month'}
        />
      </div>

      {loading && <div className="maf2-loading">Loading…</div>}

      {!loading && (
        <>
          {/* KPI strip */}
          <div className="maf2-kpi-row">
            <div className="maf2-kpi-card maf2-kpi--appr-recv">
              <div className="maf2-kpi-icon" style={{ background: 'rgba(99,102,241,0.12)' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="#6366f1" strokeWidth="2"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"/></svg>
              </div>
              <div>
                <div className="maf2-kpi-label">Appreciations Received</div>
                <div className="maf2-kpi-value" style={{ color: '#6366f1' }}>{totalAppr}</div>
              </div>
            </div>
            <div className="maf2-kpi-card maf2-kpi--appr-given">
              <div className="maf2-kpi-icon" style={{ background: 'rgba(16,185,129,0.12)' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div>
                <div className="maf2-kpi-label">Appreciations Given</div>
                <div className="maf2-kpi-value" style={{ color: '#10b981' }}>{members.reduce((s, m) => s + m.appr_given, 0)}</div>
              </div>
            </div>
            <div className="maf2-kpi-card maf2-kpi--fb-recv">
              <div className="maf2-kpi-icon" style={{ background: 'rgba(6,182,212,0.12)' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div>
                <div className="maf2-kpi-label">Feedback Received</div>
                <div className="maf2-kpi-value" style={{ color: '#06b6d4' }}>{totalFb}</div>
              </div>
            </div>
            <div className="maf2-kpi-card maf2-kpi--fb-given">
              <div className="maf2-kpi-icon" style={{ background: 'rgba(249,115,22,0.12)' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M22 2L11 13" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="#f97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <div>
                <div className="maf2-kpi-label">Feedback Given</div>
                <div className="maf2-kpi-value" style={{ color: '#f97316' }}>{members.reduce((s, m) => s + m.fb_given, 0)}</div>
              </div>
            </div>
          </div>

          {/* Member table */}
          {members.length === 0 ? (
            <div className="maf2-empty">{memberSearch ? `No members match “${memberSearch}”.` : 'No team members found for this scope.'}</div>
          ) : (
            <div className="maf2-table-wrap">
              {/* Header lives outside the scrollable area (unlike a native
                  <table>'s sticky thead) so the scrollbar only ever spans
                  the data rows, matching Team Activity's table below —
                  scrolling this list no longer visually "starts from" the
                  header. Div/grid instead of <table> because that split
                  needs the header and body to size their columns from a
                  shared explicit template rather than each independently
                  auto-sizing, which is what rowSpan/colSpan would do here. */}
              {/* Horizontal scroll wraps both header and body together so
                  they always move in sync if the fixed column widths ever
                  exceed the card's available width — only vertical scroll
                  is confined to the body (.maf2-tbody-scroll below). */}
              <div className="maf2-hscroll">
                <div className="maf2-thead" style={{ paddingRight: maf2ScrollbarW }}>
                  <div className="maf2-th maf2-th-sno" style={{ gridColumn: 1, gridRow: '1 / span 2' }}>Sl No</div>
                  <div className="maf2-th maf2-th-employee" style={{ gridColumn: 2, gridRow: '1 / span 2' }}>Employee</div>
                  <div className="maf2-th maf2-th-group" style={{ gridColumn: '3 / span 2', gridRow: 1 }}>Appreciations</div>
                  <div className="maf2-th maf2-th-group" style={{ gridColumn: '5 / span 2', gridRow: 1 }}>Feedback</div>
                  <div className="maf2-th maf2-th-group" style={{ gridColumn: '7 / span 2', gridRow: 1 }}>Last Appreciation</div>
                  <div className="maf2-th maf2-th-group" style={{ gridColumn: '9 / span 2', gridRow: 1 }}>Last Feedback</div>
                  {[3, 4, 5, 6, 7, 8, 9, 10].map(col => (
                    <div key={col} className="maf2-th maf2-th-sub" style={{ gridColumn: col, gridRow: 2 }}>
                      {col % 2 === 1 ? 'Received' : 'Given'}
                    </div>
                  ))}
                </div>
                <div className="maf2-tbody-scroll" ref={maf2TbodyRef}>
                  {members.map((m, idx) => {
                    const ini = initials(m.name);
                    const color = avatarColor(m.user_id);
                    return (
                      <div key={m.user_id} className="maf2-row">
                        <div className="maf2-td maf2-td-sno">{idx + 1}</div>
                        <div className="maf2-td maf2-td-employee">
                          <div className="maf2-member">
                            <div className="maf2-avatar" style={{ background: m.photo_url ? 'transparent' : color }}>
                              {m.photo_url
                                ? <img src={m.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                : ini}
                            </div>
                            <span className="maf2-member-name">{m.name}</span>
                          </div>
                        </div>
                        <div className="maf2-td maf2-td-num" style={{ color: '#6366f1' }}>{m.appr_received}</div>
                        <div className="maf2-td maf2-td-num" style={{ color: '#10b981' }}>{m.appr_given}</div>
                        <div className="maf2-td maf2-td-num" style={{ color: '#06b6d4' }}>{m.fb_received}</div>
                        <div className="maf2-td maf2-td-num" style={{ color: '#f97316' }}>{m.fb_given}</div>
                        <div className="maf2-td maf2-td-date" style={{ color: daysColor(m.appr_received_days_ago) }}>{daysAgoLabel(m.appr_received_days_ago)}</div>
                        <div className="maf2-td maf2-td-date" style={{ color: daysColor(m.appr_given_days_ago) }}>{daysAgoLabel(m.appr_given_days_ago)}</div>
                        <div className="maf2-td maf2-td-date" style={{ color: daysColor(m.fb_received_days_ago) }}>{daysAgoLabel(m.fb_received_days_ago)}</div>
                        <div className="maf2-td maf2-td-date" style={{ color: daysColor(m.fb_given_days_ago) }}>{daysAgoLabel(m.fb_given_days_ago)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="maf2-count">Showing {members.length} employee{members.length !== 1 ? 's' : ''}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Page component ─────────────────────────────────────────────────────────────
export default function TeamDashboard({ managerMode = false }) {
  const { appId } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const { getToken } = useAuth();
  const { me, canManage, canWrite, app, searchQuery } = useWorkspace();
  const memberSearch = (searchQuery || '').toLowerCase().trim();
  const showAdminScope = canWrite || app?.iAmOwner;

  const [data, setData]             = useState(null);
  const [openReview, setOpenReview] = useState(null); // { id, type }
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [taskStats, setTaskStats]   = useState(null);
  const [tsTab, setTsTab]           = useState('daily');

  const [memberActivity, setMemberActivity] = useState([]);
  const [orgDepartments, setOrgDepartments] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityRoleFilter, setActivityRoleFilter] = useState('');
  const [activityDeptFilter, setActivityDeptFilter] = useState('');
  const [activityPeriod, setActivityPeriod] = useState('today');
  const defaultActivityMonth = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
  const [activityMonth, setActivityMonth] = useState(defaultActivityMonth);
  const activityMonthOpts = last12MonthOptions();
  // scope: 'direct' | 'all' | 'admin' | null (null = not yet determined)
  const [scope, setScope] = useState(null);
  const [selectedManagerId, setSelectedManagerId] = useState(null);
  const [pendingBusinessReviews, setPendingBusinessReviews] = useState(0);
  // Capability flags derived from the first API call
  const [hasDirectReportees,   setHasDirectReportees]   = useState(false);
  const [hasIndirectReportees, setHasIndirectReportees] = useState(false);
  const [scopeResolved, setScopeResolved] = useState(false);

  const defaultBizMonth = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; })();
  const [bizDashboard, setBizDashboard] = useState(null);
  const [bizLoading, setBizLoading]     = useState(false);
  const [bizMonth, setBizMonth]         = useState(defaultBizMonth);

  // Reset sub-manager filter when scope changes
  useEffect(() => {
    setSelectedManagerId(null);
  }, [scope]);

  // On mount, determine scope and capabilities from a single 'direct' probe call
  useEffect(() => {
    if (!managerMode) return;
    api.getManagerDashboard(appId, 'direct')
      .then(d => {
        const hasDirect   = d.has_direct_reportees   ?? !!(d.members && d.members.length > 0);
        const hasIndirect = d.has_indirect_reportees ?? false;
        setHasDirectReportees(hasDirect);
        setHasIndirectReportees(hasIndirect);
        // Choose default scope — admins/owners default to the whole org,
        // not just their own direct reportees (matches the catch-path fallback below)
        if (showAdminScope) {
          setScope('admin');
        } else if (hasDirect) {
          setScope('direct');
        } else {
          setScope('direct');
        }
        setScopeResolved(true);
      })
      .catch(() => {
        setScope(showAdminScope ? 'admin' : 'direct');
        setScopeResolved(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, managerMode]);

  const fetchDashboard = managerMode ? api.getManagerDashboard : api.getTeamDashboard;
  const fetchTaskStats = managerMode ? api.getManagerTaskStats  : api.getTeamTaskStats;
  const fetchActivity  = managerMode ? api.getManagerActivityByMember : api.getTeamActivityByMember;

  const activeScope = managerMode ? scope : undefined;

  useEffect(() => {
    if (!managerMode) return;
    api.businessReviewDashboard(appId, {})
      .then(d => setPendingBusinessReviews((d.pending || []).length))
      .catch(() => {});
  }, [api, appId, managerMode]);

  useEffect(() => {
    if (!managerMode || scope === null) return;
    setBizDashboard(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, selectedManagerId]);

  useEffect(() => {
    if (!managerMode || scope === null) return;
    loadBizDashboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managerMode, bizMonth, scope, selectedManagerId]);

  async function loadBizDashboard() {
    setBizLoading(true);
    try {
      const params = { month: bizMonth };
      if (scope === 'direct') params.manager_id = me.id;
      else if (scope === 'all' && selectedManagerId) params.manager_id = selectedManagerId;
      const dashData = await api.businessReviewDashboard(appId, params);
      setBizDashboard(dashData);
    } catch {
      // silently fail
    } finally {
      setBizLoading(false);
    }
  }

  useEffect(() => {
    if (managerMode && scope === null) return;
    // Picking "Entire Team" reveals the "All Managers" sub-select right
    // below it — a natural next click that fires a second request before
    // the first (broader, more expensive) one has resolved. With no
    // cancellation, an in-flight "Entire Team" response can land after the
    // "All Managers"-scoped one and silently overwrite it, so every widget
    // on the page (they all read from this single `data` fetch) ends up
    // showing a mix of two different scopes' numbers.
    let ignore = false;
    setLoading(true);
    (managerMode ? api.getManagerDashboard(appId, activeScope, undefined, selectedManagerId) : api.getTeamDashboard(appId))
      .then(d => {
        if (ignore) return;
        setData(d);
        setLoading(false);
        setIsDemoMode(!d.members || d.members.length === 0);
      })
      .catch(err => {
        if (ignore) return;
        console.error('Initial fetch error:', err);
        setError('Failed to load dashboard data');
        setLoading(false);
      });
    return () => { ignore = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, managerMode, scope, selectedManagerId]);

  useEffect(() => {
    let mounted = true;
    getToken().then(token => {
      if (!mounted) return;
      const s = getSocket(token);
      const handler = ({ online }) => { if (mounted) setOnlineUserIds((online || []).map(String)); };
      s.on('presence_update', handler);
      const doJoin = () => {
        if (s._joinedAppId !== appId) { s._joinedAppId = appId; s.emit('join', { appId }); }
        s.emit('get_presence');
      };
      if (s.connected) doJoin(); else s.once('connect', doJoin);
      return () => s.off('presence_update', handler);
    });
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, getToken]);

  useEffect(() => {
    if (managerMode && scope === null) return;
    let ignore = false;
    const iv = setInterval(() => {
      (managerMode ? api.getManagerDashboard(appId, activeScope, undefined, selectedManagerId) : api.getTeamDashboard(appId))
        .then(d => {
          if (ignore) return;
          setData(d);
          setIsDemoMode(!d.members || d.members.length === 0);
          setLoading(false);
        })
        .catch(err => console.error('Polling error:', err));
    }, 30000);
    return () => { ignore = true; clearInterval(iv); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, managerMode, scope, selectedManagerId]);

  useEffect(() => {
    if (!appId) { setIsDemoMode(true); setLoading(false); }
  }, [appId]);

  useEffect(() => {
    if (!appId || (managerMode && scope === null)) return;
    (managerMode ? api.getManagerTaskStats(appId, activeScope, selectedManagerId) : api.getTeamTaskStats(appId))
      .then(setTaskStats).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId, managerMode, scope, selectedManagerId]);

  useEffect(() => {
    if (!appId || (managerMode && scope === null)) return;
    // Clicking through the Today/Yesterday/This Week/Last Week tabs fires a
    // new request per click with no cancellation of the previous one — on a
    // slower network an earlier tab's response can resolve after a later
    // one, silently overwriting the currently-selected tab's counts with a
    // stale period's data. `ignore` drops any response that isn't for the
    // period still selected when it comes back.
    let ignore = false;
    setActivityLoading(true);
    const params = { period: activityPeriod };
    if (activityPeriod === 'month') params.month = activityMonth;
    if (managerMode && activeScope) params.scope = activeScope;
    if (managerMode && selectedManagerId) params.viewAs = selectedManagerId;
    fetchActivity(appId, params)
      .then(d => { if (!ignore) { setMemberActivity(d.members || []); setActivityLoading(false); } })
      .catch(() => { if (!ignore) setActivityLoading(false); });
    return () => { ignore = true; };
  }, [appId, activityPeriod, activityMonth, scope, selectedManagerId]);

  // Full org department list for the filter dropdown — memberActivity only
  // has departments for people with activity in the selected period/scope,
  // so a department with no activity yet would silently disappear from the
  // filter's options if we sourced it from memberActivity alone.
  useEffect(() => {
    if (!appId) return;
    api.departments.list(appId)
      .then(d => setOrgDepartments(d.items || []))
      .catch(() => {});
  }, [appId, api]);

  if (loading) return (
    <div className="tdb-page" style={{ justifyContent: 'center', alignItems: 'center' }}>
      Loading team dashboard…
    </div>
  );
  if (error) return (
    <div className="tdb-page" style={{ justifyContent: 'center', alignItems: 'center', color: 'var(--neg)' }}>
      {error}
    </div>
  );
  if (!data) return null;

  // ── Review detail — replace full page (same pattern as MyDashboard) ──
  if (openReview) {
    const onBack = () => setOpenReview(null);
    if (openReview.type === 'daily')   return <DailyCheckinView   reviewId={openReview.id} appId={appId} onBack={onBack} />;
    if (openReview.type === 'weekly')  return <WeeklyCheckinView  reviewId={openReview.id} appId={appId} onBack={onBack} />;
    if (openReview.type === 'monthly') return <MonthlyReviewView  reviewId={openReview.id} appId={appId} onBack={onBack} />;
  }

  const activeData   = isDemoMode ? MOCK_DATA : data;
  const { members: activeMembers, team_totals: activeTotals } = activeData;
  const minHoursPerDay  = activeData.min_hours_per_day  ?? 0;
  const minHoursPerWeek = activeData.min_hours_per_week ?? 0;
  const showEmpty    = !isDemoMode && activeMembers.length === 0;

  // ── Empty state ──
  if (showEmpty) {
    return (
      <div className="tdb-page">
        <div className="tdb-card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center', gap: 20 }}>
          <div style={{ fontSize: '48px' }}>👥</div>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>No Reportees Registered</h2>
          <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '500px', margin: 0, lineHeight: 1.6 }}>
            Your account does not have any direct or skip-level reportees assigned in the database hierarchy.
            Configure manager assignments and roles in your organization settings to see real team data.
          </p>
          <button className="tdb-demo-toggle-btn" style={{ padding: '12px 24px', fontSize: '13px' }} onClick={() => setIsDemoMode(true)}>
            Enable Simulated Demo Preview
          </button>
        </div>
      </div>
    );
  }

  // ── Derived data ──
  // When the top navbar search has a member name in it, scope every widget below to just that member.
  const searchedMembers = memberSearch
    ? activeMembers.filter(m => displayName(m.user).toLowerCase().includes(memberSearch))
    : activeMembers;
  const isMemberFiltered = !!memberSearch;

  const tt = isMemberFiltered
    ? searchedMembers.reduce((acc, m) => ({
        completed:   acc.completed   + (m.tasks.done || 0),
        in_progress: acc.in_progress + (m.tasks.in_progress || 0),
        on_hold:     acc.on_hold     + (m.tasks.blocked || 0),
        delayed:     acc.delayed     + (m.tasks.overdue || 0),
        total:       acc.total       + (m.tasks.total || 0),
      }), { completed: 0, in_progress: 0, on_hold: 0, delayed: 0, total: 0 })
    : activeTotals.team_tasks;
  const onlineNow = searchedMembers.filter(m => onlineUserIds.includes(String(m.user_id))).length;
  const overallProgress = tt.total > 0 ? Math.round((tt.completed / tt.total) * 100) : 0;
  const taskCompletion  = overallProgress;

  const leaderboard = [...searchedMembers]
    .sort((a, b) => (b.glos_score?.total || 0) - (a.glos_score?.total || 0));

  const pendingManagerReviews = data?.pending_manager_reviews ?? 0;

  const totalTasksAssigned = searchedMembers.reduce((acc, m) => acc + m.tasks.total, 0);

  const workloadColors = [CLR.red, CLR.amber, CLR.green, CLR.blue, CLR.sky];

  const getWorkloadColor = (hrs, minWeekHrs) => {
    const threshold = minWeekHrs || 40;
    if (hrs >= threshold) return CLR.red;
    if (hrs >= threshold * 0.2) return CLR.amber;
    return CLR.green;
  };
  const getWorkloadPriority = (hrs, minWeekHrs) => {
    const threshold = minWeekHrs || 40;
    if (hrs >= threshold) return 0;
    if (hrs >= threshold * 0.2) return 1;
    return 2;
  };
  const workloadMembers = [...searchedMembers].sort((a, b) => {
    const aHrs = (a.tasks.not_started_est_hrs ?? 0) + (a.tasks.in_progress_est_hrs ?? 0);
    const bHrs = (b.tasks.not_started_est_hrs ?? 0) + (b.tasks.in_progress_est_hrs ?? 0);
    const aPri = getWorkloadPriority(aHrs, minHoursPerWeek);
    const bPri = getWorkloadPriority(bHrs, minHoursPerWeek);
    if (aPri !== bPri) return aPri - bPri;
    return bHrs - aHrs;
  });

  // podium order: [rank-2, rank-1, rank-3]
  const podiumTop3 = leaderboard.slice(0, 3);
  const podiumOrder = podiumTop3.length >= 3
    ? [{ ...podiumTop3[1], rank: 2 }, { ...podiumTop3[0], rank: 1 }, { ...podiumTop3[2], rank: 3 }]
    : podiumTop3.map((m, i) => ({ ...m, rank: i + 1 }));
  const leaderboardRest = leaderboard.slice(3);

  const medalColors = { 1: '#f0a93b', 2: '#94a3b8', 3: '#b87333' };
  const rankLabel   = { 1: '🥇', 2: '🥈', 3: '🥉' };

  return (
    <div className="tdb-page">

      {/* ── Demo banner ── */}
      {isDemoMode && (
        <div className="tdb-demo-banner">
          <div className="tdb-demo-banner-content">
            <span className="tdb-demo-banner-icon">⚡</span>
            <div className="tdb-demo-banner-text">
              <div className="tdb-demo-banner-title">
                <span className="tdb-demo-badge">Simulated Demo</span>
                <span>Experience the Premium Dashboard Preview</span>
              </div>
              <div className="tdb-demo-banner-desc">
                No direct reportees were found under your account. We're showing simulated team data so you
                can evaluate the interactive leaderboard, attendance, workload, and performance KPIs.
              </div>
            </div>
          </div>
          {data.members && data.members.length > 0 ? (
            <button className="tdb-demo-toggle-btn" onClick={() => setIsDemoMode(false)}>Show Real Data</button>
          ) : (
            <button className="tdb-demo-toggle-btn" onClick={() => setIsDemoMode(false)}>View Empty State</button>
          )}
        </div>
      )}

      {/* ── Hero Banner ── */}
      <div className="tdb-hero-banner">
        <div className="tdb-hero-glow" />
        <div className="tdb-hero-content">
          <div className="tdb-hero-title-row">
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div className="tdb-hero-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div>
                <div className="tdb-hero-title">{managerMode ? 'Manager Dashboard' : 'Team Dashboard'}</div>
                <div className="tdb-hero-subtitle">Real-time team performance and collaboration insights</div>
              </div>
            </div>
          </div>
          <div className="tdb-hero-bottom-row">
            <div className="tdb-hero-stat">
              <div className="tdb-hero-stat-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="tdb-hero-stat-info">
                <span className="tdb-hero-stat-val">{isMemberFiltered ? searchedMembers.length : activeTotals.member_count}</span>
                <span className="tdb-hero-stat-lbl">Team Members</span>
              </div>
            </div>
            {!managerMode && (
            <div className="tdb-hero-stat" title="Members currently active in the app (real-time WebSocket presence)">
              <div className="tdb-hero-stat-icon" style={{ color: CLR.green }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="tdb-hero-stat-info">
                <span className="tdb-hero-stat-val" style={{ color: CLR.green }}>{onlineNow}</span>
                <span className="tdb-hero-stat-lbl">Online Now</span>
              </div>
            </div>
            )}
            <HeroTooltip className="tdb-hero-stat" text="% of team tasks completed out of total tasks due this month">
              <div className="tdb-hero-stat-icon" style={{ color: CLR.purple }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <div className="tdb-hero-stat-info">
                <span className="tdb-hero-stat-val" style={{ color: CLR.purple }}>{overallProgress}%</span>
                <span className="tdb-hero-stat-lbl">Overall Progress</span>
              </div>
            </HeroTooltip>
            <HeroTooltip className="tdb-hero-stat" text="% of completed tasks that were finished on or before their due date">
              <div className="tdb-hero-stat-icon" style={{ color: CLR.green }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <div className="tdb-hero-stat-info">
                <span className="tdb-hero-stat-val" style={{ color: CLR.green }}>{isDemoMode ? 72 : (activeTotals.ontime_completion ?? 0)}%</span>
                <span className="tdb-hero-stat-lbl">On-Time Completion</span>
              </div>
            </HeroTooltip>
            {managerMode && pendingManagerReviews > 0 && (
              <HeroTooltip className="tdb-hero-stat" text="Team members awaiting your manager performance review" style={{ cursor: 'pointer' }} onClick={() => navigate(`/apps/${appId}/performance`)}>
                <div className="tdb-hero-stat-icon" style={{ color: CLR.red }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="12" y1="11" x2="12" y2="17"/>
                    <line x1="9" y1="14" x2="15" y2="14"/>
                  </svg>
                </div>
                <div className="tdb-hero-stat-info">
                  <span className="tdb-hero-stat-val" style={{ color: CLR.red }}>{pendingManagerReviews}</span>
                  <span className="tdb-hero-stat-lbl">Reviews Pending</span>
                </div>
              </HeroTooltip>
            )}
            {managerMode && pendingBusinessReviews > 0 && (
              <HeroTooltip className="tdb-hero-stat" text="Open daily/weekly/monthly check-ins awaiting completion" style={{ cursor: 'pointer' }} onClick={() => navigate(`/apps/${appId}/business-reviews`)}>
                <div className="tdb-hero-stat-icon" style={{ color: CLR.amber }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
                    <rect x="9" y="3" width="6" height="4" rx="1"/>
                    <path d="M9 12h6M9 16h4"/>
                  </svg>
                </div>
                <div className="tdb-hero-stat-info">
                  <span className="tdb-hero-stat-val" style={{ color: CLR.amber }}>{pendingBusinessReviews}</span>
                  <span className="tdb-hero-stat-lbl">Business Reviews Pending</span>
                </div>
              </HeroTooltip>
            )}
            {managerMode && (
              <div style={{ display: 'flex', gap: 8 }}>
                <select
                  value={scope || ''}
                  onChange={e => { setScope(e.target.value); setSelectedManagerId(null); }}
                  className="tdb-scope-select"
                >
                  {hasDirectReportees && <option value="direct">Direct Reportees</option>}
                  {hasIndirectReportees && <option value="all">Entire Team</option>}
                  {showAdminScope && <option value="admin">All Members</option>}
                </select>
                {scope === 'all' && data?.sub_managers?.length > 0 && (
                  <ManagerSelect
                    value={selectedManagerId ?? ''}
                    onChange={v => setSelectedManagerId(v ? Number(v) : null)}
                    options={data.sub_managers}
                  />
                )}
              </div>
            )}
            {!managerMode && (
              <div className="tdb-team-selector">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                </svg>
                {activeTotals.org_name || 'Organisation'}
              </div>
            )}
          </div>
        </div>
        <div className="tdb-hero-illustration">
          <HeroIllustration />
        </div>
      </div>


      {/* ── Middle Row — 3 cols ── */}
      <div className="tdb-middle-row">

        {/* Task Status Overview */}
        <div className="tdb-card tdb-taskstatus-card">
          <h3 className="tdb-section-title">Task Status Overview</h3>
          <div className="tdb-task-status-body">
            <div className="tdb-task-donut-wrap">
              <TaskDonut
                completed={tt.completed}
                inProgress={tt.in_progress}
                onHold={tt.on_hold || 0}
                overdue={tt.delayed || 0}
                total={tt.total}
              />
              <div className="tdb-task-donut-center">
                <span className="tdb-task-donut-val">{tt.total}</span>
                <span className="tdb-task-donut-lbl">Total Tasks</span>
              </div>
            </div>
            <div className="tdb-task-legend">
              {[
                { label: 'Completed', count: tt.completed,   color: CLR.green  },
                { label: 'In Progress', count: tt.in_progress, color: CLR.blue   },
                { label: 'Blocked',   count: tt.on_hold || 0, color: CLR.amber  },
                { label: 'Overdue',   count: tt.delayed || 0, color: CLR.red    },
              ].map(({ label, count, color }) => (
                <div key={label} className="tdb-task-legend-row">
                  <div className="tdb-task-legend-left">
                    <div className="tdb-legend-dot" style={{ background: color }} />
                    {label}
                  </div>
                  <div className="tdb-task-legend-right">{count}</div>
                </div>
              ))}
            </div>
          </div>
          <button className="tdb-view-all" onClick={() => navigate(`/apps/${appId}/tasks`)}>
            View All Tasks
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>

        {/* Team Workload */}
        <div className="tdb-card">
          <h3 className="tdb-section-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Team Workload
            {minHoursPerWeek > 0 && (
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>
                (min {minHoursPerWeek}h/week)
              </span>
            )}
          </h3>
          <div className="tdb-workload-list tdb-workload-scroll">
            {workloadMembers.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>No members match “{searchQuery}”.</div>
            ) : (() => {
              const maxHrs = Math.max(...workloadMembers.map(m =>
                (m.tasks.not_started_est_hrs ?? 0) + (m.tasks.in_progress_est_hrs ?? 0)
              ), minHoursPerWeek || 1);
              return workloadMembers.map((m, i) => {
                const totalHrsRaw = (m.tasks.not_started_est_hrs ?? 0) + (m.tasks.in_progress_est_hrs ?? 0);
                const totalHrs    = Math.round(totalHrsRaw * 100) / 100;
                const barPct      = Math.round((totalHrsRaw / maxHrs) * 100);
                const barColor    = getWorkloadColor(totalHrsRaw, minHoursPerWeek);
                return (
                  <div key={m.user.id} className="tdb-workload-item">
                    <div className="tdb-workload-name-row">
                      <span className="tdb-workload-sl">{i + 1}</span>
                      <div className="tdb-avatar sm" style={{ background: m.user.photo_url ? 'transparent' : avatarColor(m.user.id) }}>
                        {m.user.photo_url
                          ? <img src={m.user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          : displayName(m.user)[0]?.toUpperCase()}
                      </div>
                      <span className="tdb-workload-name" title={displayName(m.user)}>
                        {displayName(m.user)}
                      </span>
                    </div>
                    <div className="tdb-workload-bar-row">
                      <div className="tdb-workload-bar-wrap">
                        <div className="tdb-workload-bar" style={{ width: `${barPct}%`, background: barColor }} />
                      </div>
                      <span className="tdb-workload-val" style={{ color: barColor }}>
                        {fmtHours(Math.round(totalHrs * 60))}
                      </span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>
        </div>


      </div>

      {/* ── Team Activity by Employee ── */}
      {(() => {

        const allRoles = [...new Set(memberActivity.map(m => m.role).filter(Boolean))].sort();
        const allDepts = orgDepartments.length
          ? [...new Set(orgDepartments.map(d => d.name).filter(Boolean))].sort()
          : [...new Set(memberActivity.map(m => m.department).filter(Boolean))].sort();

        const filtered = memberActivity.filter(m => {
          if (memberSearch && !m.name.toLowerCase().includes(memberSearch)) return false;
          if (activitySearch && !m.name.toLowerCase().includes(activitySearch.toLowerCase())) return false;
          if (activityRoleFilter && m.role !== activityRoleFilter) return false;
          if (activityDeptFilter && m.department !== activityDeptFilter) return false;
          return true;
        });

        // ── Activity summary totals ──
        const totalAssigned  = filtered.reduce((s, m) => s + (m.created || 0), 0);
        const totalCompleted = filtered.reduce((s, m) => s + (m.completed || 0), 0);
        const totalHours     = filtered.reduce((s, m) => s + (m.hours || 0), 0);
        const totalMinutes   = filtered.reduce((s, m) => s + (m.minutes || Math.round((m.hours || 0) * 60)), 0);
        const ontimeMembers  = filtered.filter(m => m.on_time_pct != null);
        const avgOnTime      = ontimeMembers.length
          ? Math.round(ontimeMembers.reduce((s, m) => s + m.on_time_pct, 0) / ontimeMembers.length)
          : null;

        return (
          <div className="tdb-card tdb-taskstats-card" style={{ overflow: 'visible' }}>
            <div className="tdb-taskstats-header">
              <div className="tdb-activity-header-left">
                <h3 className="tdb-section-title" style={{ margin: 0 }}>Team Activity</h3>
                {/* Search */}
                <div className="tdb-activity-search-wrap">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    className="tdb-activity-search"
                    placeholder="Search employee…"
                    value={activitySearch}
                    onChange={e => setActivitySearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="tdb-activity-filters">
                {/* Period tabs */}
                <div className="tdb-period-tabs">
                  {[{ key: 'today', lbl: 'Today' }, { key: 'yesterday', lbl: 'Yesterday' }, { key: 'week', lbl: 'This Week' }, { key: 'last_week', lbl: 'Last Week' }].map(p => (
                    <button key={p.key} className={`tdb-period-tab${activityPeriod === p.key ? ' active' : ''}`} onClick={() => setActivityPeriod(p.key)}>{p.lbl}</button>
                  ))}
                </div>
                {/* Month picker — kept outside .tdb-period-tabs whose overflow:hidden clips the dropdown */}
                <MonthPickerGroup
                  value={activityMonth}
                  active={activityPeriod === 'month'}
                  options={activityMonthOpts}
                  onChange={val => { setActivityMonth(val); setActivityPeriod('month'); }}
                  defaultValue={defaultActivityMonth}
                  label={activityPeriod === 'month' && activityMonth !== defaultActivityMonth
                    ? activityMonthOpts.find(o => o.val === activityMonth)?.lbl?.replace(/\s\d{4}$/, '') || 'Month'
                    : 'This Month'}
                />
                {/* Role filter */}
                {allRoles.length > 0 && (
                  <FilterSelect
                    value={activityRoleFilter}
                    onChange={setActivityRoleFilter}
                    options={allRoles}
                    placeholder="All Roles"
                  />
                )}
                {/* Department filter */}
                {allDepts.length > 0 && (
                  <FilterSelect
                    value={activityDeptFilter}
                    onChange={setActivityDeptFilter}
                    options={allDepts}
                    placeholder="All Departments"
                  />
                )}
              </div>
            </div>
            {/* ── Activity summary cards ── */}
            <div className="tdb-activity-summary-cards">
              <div className="tdb-act-summary-card" style={{ borderTop: `3px solid ${CLR.accent}` }}>
                <div className="tdb-act-summary-icon" style={{ background: `${CLR.accent}18`, color: CLR.accent }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="2" width="6" height="4" rx="1"/><path d="M4 6h16v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6z"/><path d="M4 10v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10"/>
                    <line x1="9" y1="15" x2="15" y2="15"/><line x1="12" y1="12" x2="12" y2="18"/>
                  </svg>
                </div>
                <div className="tdb-act-summary-body">
                  <div className="tdb-act-summary-val" style={{ color: CLR.accent }}>{activityLoading ? '—' : totalAssigned}</div>
                  <div className="tdb-act-summary-lbl">Tasks Assigned / Created</div>
                </div>
              </div>
              <div className="tdb-act-summary-card" style={{ borderTop: `3px solid ${CLR.green}` }}>
                <div className="tdb-act-summary-icon" style={{ background: `${CLR.green}18`, color: CLR.green }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                </div>
                <div className="tdb-act-summary-body">
                  <div className="tdb-act-summary-val" style={{ color: CLR.green }}>{activityLoading ? '—' : totalCompleted}</div>
                  <div className="tdb-act-summary-lbl">Total Completed Tasks</div>
                </div>
              </div>
              <div className="tdb-act-summary-card" style={{ borderTop: `3px solid ${CLR.purple}` }}>
                <div className="tdb-act-summary-icon" style={{ background: `${CLR.purple}18`, color: CLR.purple }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div className="tdb-act-summary-body">
                  <div className="tdb-act-summary-val" style={{ color: CLR.purple }}>{activityLoading ? '—' : fmtHours(totalMinutes)}</div>
                  <div className="tdb-act-summary-lbl">Total Hours Logged</div>
                </div>
              </div>
              <div className="tdb-act-summary-card" style={{ borderTop: `3px solid ${avgOnTime == null ? CLR.amber : avgOnTime >= 80 ? CLR.green : avgOnTime >= 50 ? CLR.amber : CLR.red}` }}>
                <div className="tdb-act-summary-icon" style={{ background: `${CLR.amber}18`, color: CLR.amber }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                  </svg>
                </div>
                <div className="tdb-act-summary-body">
                  <div className="tdb-act-summary-val" style={{ color: avgOnTime == null ? 'var(--text-muted)' : avgOnTime >= 80 ? CLR.green : avgOnTime >= 50 ? CLR.amber : CLR.red }}>
                    {activityLoading ? '—' : avgOnTime != null ? avgOnTime + '%' : '0%'}
                  </div>
                  <div className="tdb-act-summary-lbl">On-Time Completion Rate</div>
                </div>
              </div>
            </div>

            <div className="tdb-activity-member-table">
              <div className="tdb-activity-member-thead has-planned">
                <span className="tdb-am-col-sno">Sl No</span>
                <span className="tdb-am-col-name">EMPLOYEE</span>
                <span className="tdb-am-col-meta">ROLE / DEPT</span>
                <span className="tdb-am-col-stat">ASSIGNED</span>
                <span className="tdb-am-col-stat">COMPLETED</span>
                <span className="tdb-am-col-stat" style={{ flexDirection: 'column', gap: 1 }}>
                  <span>HOURS</span>
                  {(() => {
                    const minH = (activityPeriod === 'today' || activityPeriod === 'yesterday') ? minHoursPerDay : (activityPeriod === 'week' || activityPeriod === 'last_week') ? minHoursPerWeek : 0;
                    return minH > 0 ? <span style={{ fontSize: 9, fontWeight: 400, color: 'var(--text-muted)' }}>min {minH}h</span> : null;
                  })()}
                </span>
                <span className="tdb-am-col-stat">PLANNED</span>
                <span className="tdb-am-col-stat">ON TIME</span>
              </div>
              <div className="tdb-activity-member-tbody">
                {activityLoading ? (
                  <div className="tdb-am-empty">Loading…</div>
                ) : filtered.length === 0 ? (
                  <div className="tdb-am-empty">{memberActivity.length === 0 ? 'No activity data for this period.' : 'No employees match the filter.'}</div>
                ) : filtered.map((m, idx) => (
                  <div key={m.user_id} className="tdb-activity-member-row has-planned">
                    <span className="tdb-am-col-sno">{idx + 1}</span>
                    <span className="tdb-am-col-name">
                      <div className="tdb-avatar sm" style={{ background: m.photo_url ? 'transparent' : avatarColor(m.user_id), flexShrink: 0 }}>
                        {m.photo_url
                          ? <img src={m.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                          : (m.name || '?')[0].toUpperCase()}
                      </div>
                      <span className="tdb-am-name">{m.name}</span>
                    </span>
                    <span className="tdb-am-col-meta">
                      {m.role ? <span className="tdb-am-role">{m.role}</span> : null}
                      {m.department ? <span className="tdb-am-dept">{m.department}</span> : null}
                      {!m.role && !m.department ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span> : null}
                    </span>
                    <span className="tdb-am-col-stat">
                      <span className="tdb-am-badge created">{m.created}</span>
                    </span>
                    <span className="tdb-am-col-stat">
                      <span className="tdb-am-badge completed">{m.completed}</span>
                    </span>
                    <span className="tdb-am-col-stat">
                      {(() => {
                        const minH = (activityPeriod === 'today' || activityPeriod === 'yesterday')
                          ? minHoursPerDay
                          : (activityPeriod === 'week' || activityPeriod === 'last_week')
                            ? minHoursPerWeek
                            : 0;
                        const below = minH > 0 && (m.hours || 0) < minH;
                        return (
                          <span className="tdb-am-badge hours" style={below ? { background: '#fef3c720', color: '#d97706', border: '1px solid #d9770640' } : {}}>
                            {below && <span style={{ marginRight: 3 }}>⚠</span>}{fmtHours(m.minutes != null ? m.minutes : Math.round((m.hours || 0) * 60))}
                          </span>
                        );
                      })()}
                    </span>
                    <span className="tdb-am-col-stat">
                      {m.planned_minutes != null && m.planned_minutes > 0
                        ? <span className="tdb-am-badge hours">{fmtHours(m.planned_minutes)}</span>
                        : m.planned_hours != null && m.planned_hours > 0
                          ? <span className="tdb-am-badge hours">{fmtHours(Math.round(m.planned_hours * 60))}</span>
                          : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      }
                    </span>
                    <span className="tdb-am-col-stat">
                      {m.on_time_pct != null ? (
                        <span className="tdb-am-badge" style={{
                          background: m.on_time_pct >= 80 ? '#d1fae5' : m.on_time_pct >= 50 ? '#fef3c7' : '#fee2e2',
                          color:      m.on_time_pct >= 80 ? '#065f46' : m.on_time_pct >= 50 ? '#92400e' : '#991b1b',
                        }}>{m.on_time_pct}%</span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Business Reviews Detail (manager only) ── */}
      {managerMode && !isDemoMode && (
        <div className="tdb-card" style={{ overflow: 'visible' }}>
          <BizReviewsTable
            dashboard={bizDashboard}
            loading={bizLoading}
            month={bizMonth}
            onMonth={setBizMonth}
            memberSearch={memberSearch}
          />
        </div>
      )}

      {/* ── Bottom Row — 3 cols ── */}
      <div className="tdb-bottom-row">

        {/* Team Leaderboard — podium */}
        <div className="tdb-card">
          <h3 className="tdb-section-title">Team Leaderboard</h3>
          {leaderboard.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>No members match “{searchQuery}”.</div>
          )}
          {podiumOrder.length > 0 && (
            <div className="tdb-podium">
              {podiumOrder.map((m) => {
                const pts = m.glos_score?.total || 0;
                return (
                  <div key={m.user.id} className="tdb-podium-item">
                    <div className="tdb-podium-rank">{rankLabel[m.rank]}</div>
                    <div className={`tdb-podium-avatar rank-${m.rank}`}
                      style={{ background: m.user.photo_url ? 'transparent' : avatarColor(m.user.id) }}>
                      {m.user.photo_url
                        ? <img src={m.user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        : displayName(m.user)[0]?.toUpperCase()}
                    </div>
                    <div className="tdb-podium-name">{displayName(m.user).split(' ')[0]}</div>
                    <div className="tdb-podium-pts">{pts} pts</div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ maxHeight: 132, overflowY: 'auto' }}>
            {leaderboardRest.map((m, i) => {
              const pts = m.glos_score?.total || 0;
              return (
                <div key={m.user.id} className="tdb-leaderboard-row">
                  <div className="tdb-leaderboard-rank">{i + 4}</div>
                  <div className="tdb-avatar sm" style={{ background: m.user.photo_url ? 'transparent' : avatarColor(m.user.id) }}>
                    {m.user.photo_url
                      ? <img src={m.user.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                      : displayName(m.user)[0]?.toUpperCase()}
                  </div>
                  <div className="tdb-leaderboard-name">{displayName(m.user)}</div>
                  <div className="tdb-leaderboard-pts">{pts} pts</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Team Mood */}
        <div className="tdb-card">
          <h3 className="tdb-section-title">Team Mood</h3>
          <div className="tdb-mood-container">
            <MoodGauge score={activeTotals.team_mood} />
            <div className="tdb-mood-score">
              {activeTotals.team_mood}<span>/10</span>
            </div>
            <div className="tdb-mood-label">
              {activeTotals.team_mood >= 8 ? 'Great Team Vibes!' : activeTotals.team_mood >= 6 ? 'Good Energy 😊' : activeTotals.team_mood > 0 ? 'Needs Attention 💬' : 'No review data yet'}
            </div>
          </div>
        </div>

        {/* 3rd card: Team Projects only for superadmin in Team Dashboard; calendar everywhere else */}
        {canManage && !managerMode ? (
          <div className="tdb-card">
            <h3 className="tdb-section-title">
              Team Projects
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>owned by team members</span>
            </h3>
            {(activeTotals.project_progress || []).length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>No projects owned by team members</div>
            ) : (
              <div className="tdb-progress-list">
                {(activeTotals.project_progress || []).map((p) => {
                  const statusMeta = {
                    completed: { label: 'Completed', color: CLR.green },
                    overdue:   { label: 'Overdue',   color: CLR.red   },
                    near_end:  { label: 'Near End',  color: CLR.amber },
                    active:    { label: 'Active',    color: CLR.blue  },
                    on_hold:   { label: 'Blocked',   color: CLR.amber },
                  }[p.status] || { label: p.status || 'Active', color: CLR.blue };
                  const barColor = statusMeta.color;
                  return (
                    <div key={p.id} className="tdb-progress-item">
                      <div className="tdb-progress-header">
                        <span style={{ maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20,
                            background: `${barColor}22`, color: barColor, whiteSpace: 'nowrap',
                          }}>{statusMeta.label}</span>
                          <span style={{ color: barColor, fontWeight: 600 }}>{p.progress}%</span>
                        </span>
                      </div>
                      <div className="tdb-progress-bar-bg">
                        <div className="tdb-progress-bar-fill" style={{ width: `${p.progress}%`, background: barColor }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <button className="tdb-view-all" onClick={() => navigate(`/apps/${appId}/projects`)}>
              View All Projects
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>
          </div>
        ) : !isDemoMode && me?.id ? (
          <div className="tdb-card" style={{ paddingBottom: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px 4px' }}>
              <h3 className="tdb-section-title" style={{ margin: 0 }}>Business Review Calendar</h3>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <span className="br-cal-legend">
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', marginRight: 4 }} />
                  Open
                </span>
                <span className="br-cal-legend">
                  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#22c55e', marginRight: 4 }} />
                  Completed
                </span>
              </div>
            </div>
            <TeamReviewCalendarWidget
              appId={appId}
              managerId={me.id}
              onOpenReview={setOpenReview}
            />
          </div>
        ) : null}

      </div>

      {/* ── Appreciations & Feedback ── */}
      {!isDemoMode && <ApprFeedSection appId={appId} api={api} scope={scope} managerMode={managerMode} selectedManagerId={selectedManagerId} memberSearch={memberSearch} />}

      {/* ── Routine Analysis ── */}
      {!isDemoMode && <TeamRoutineAnalysis appId={appId} api={api} scope={managerMode ? scope : undefined} selectedManagerId={selectedManagerId} memberSearch={memberSearch} />}

    </div>
  );
}

// ── Business Reviews helpers & components ─────────────────────────────────────

const BIZ_TYPE_LABEL = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly', other: 'Other' };
const BIZ_TYPE_ICON  = { daily: '📅', weekly: '📆', monthly: '🗓️', other: '📋' };
const BIZ_STATUS_PILL = {
  open:   { label: 'Open',   bg: '#fffbeb', color: '#b45309', border: '#fde68a' },
  closed: { label: 'Closed', bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
};

function bizFmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}


function BizReviewsTable({ dashboard, loading, month, onMonth, memberSearch }) {
  const allManagerStats = dashboard?.manager_stats || [];
  const managerStats = memberSearch
    ? allManagerStats.filter(mgr => (mgr.name || '').toLowerCase().includes(memberSearch))
    : allManagerStats;

  const defaultBizMonth = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; })();
  const bizMonthOpts = last12MonthOptions();
  const isCurrentBizMonth = month === defaultBizMonth;

  // Header sits outside .brd-tbody-scroll, so it has no scrollbar of its own
  // to reserve space for — measuring the real gutter width (rather than
  // guessing in CSS) keeps its columns pixel-aligned with the body's, same
  // fix as the Appreciations & Feedback table.
  const brdTbodyRef = useRef(null);
  const [brdScrollbarW, setBrdScrollbarW] = useState(0);
  useEffect(() => {
    const el = brdTbodyRef.current;
    if (!el) { setBrdScrollbarW(0); return; }
    setBrdScrollbarW(el.offsetWidth - el.clientWidth);
  }, [managerStats.length]);

  function TypeCounts({ data }) {
    if (!data) return <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: 12 }}>
        <span style={{ color: '#6366f1', fontWeight: 600 }}>{data.total} created</span>
        <span style={{ color: '#10b981' }}>{data.closed} done</span>
      </div>
    );
  }

  return (
    <div className="brd-wrap">
      {/* Header with title + month picker */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <h3 className="tdb-section-title" style={{ margin: 0 }}>Business Reviews</h3>
        <MonthPickerGroup
          value={month}
          active={!isCurrentBizMonth}
          options={bizMonthOpts}
          onChange={onMonth}
          defaultValue={defaultBizMonth}
          label={!isCurrentBizMonth ? bizMonthOpts.find(o => o.val === month)?.lbl?.replace(/\s\d{4}$/, '') || 'Month' : 'This Month'}
        />
      </div>

      {loading ? (
        <div className="brd-empty">Loading…</div>
      ) : managerStats.length === 0 ? (
        <div className="brd-empty">{memberSearch ? `No managers match “${memberSearch}”.` : 'No reviews found for this month.'}</div>
      ) : (
        <div className="brd2-table-wrap">
          {/* Header lives outside the scrollable area (unlike a native
              <table>'s sticky thead) so the scrollbar only ever spans the
              data rows — scrolling starts from row 1 (Sl No), not from the
              heading — matching Appreciations & Feedback. */}
          <div className="brd2-hscroll">
            <div className="brd2-thead" style={{ paddingRight: brdScrollbarW }}>
              <div className="brd2-th brd2-th-sno">Sl No</div>
              <div className="brd2-th brd2-th-manager">Manager</div>
              <div className="brd2-th">Daily Check-ins</div>
              <div className="brd2-th">Weekly</div>
              <div className="brd2-th">Monthly</div>
            </div>
            <div className="brd2-tbody-scroll" ref={brdTbodyRef}>
              {managerStats.map((mgr, idx) => {
                const initials = (mgr.name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
                return (
                  <div key={mgr.manager_user_id} className="brd2-row">
                    <div className="brd2-td brd2-td-sno">{idx + 1}</div>
                    <div className="brd2-td brd2-td-manager">
                      <div className="brd2-member">
                        <div className="brd-avatar">
                          {mgr.photo_url
                            ? <img src={mgr.photo_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                            : initials}
                        </div>
                        <span className="brd2-member-name">{mgr.name}</span>
                      </div>
                    </div>
                    <div className="brd2-td"><TypeCounts data={mgr.daily} /></div>
                    <div className="brd2-td"><TypeCounts data={mgr.weekly} /></div>
                    <div className="brd2-td"><TypeCounts data={mgr.monthly} /></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
